import { Color } from "@repo/app-types/constants/colors";
import type { DateRange } from "@repo/app-types/entities/filters";
import { Size } from "@repo/ui/helpers/helpers";
import { Pill } from "@repo/ui/Pills/pill";
import { skeletonStyle } from "@repo/ui/Skeletons/skeletons";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { IconInfoCircle } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { useId, type FC } from "react";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";
import { BaseTooltipContent } from "../../performanceReports/baseTooltipContent.js";
import { SparklineGradient } from "../../performanceReports/sparklineGradient.js";
import { computeValueChangeIndication } from "../../performanceReports/values.js";
import { DeltaPill } from "../../teammatePerformance/monthInReview/deltaPill.js";

/**
 * Shape passed to the shared {@link MetricTrendCard} for a single
 * sparkline point. `value` is the metric's already-computed score for
 * the trailing-window bucket; `null` (no data in the window) is
 * coerced to 0 before rendering so the line drops to the floor.
 */
export interface MetricTrendPoint {
  date: string;
  value: number | null;
}

interface MetricTrendCardProps {
  label: string;
  /**
   * Headline value for the visible period as an already-rendered
   * string (e.g. `"71"` for a CX score, `"62%"` for a win rate). The
   * caller decides the unit and precision so the shared component
   * stays agnostic. Pass `"—"` for the no-data case.
   */
  headlineValue: string;
  /**
   * Signed difference between current and prior period in the
   * metric's native units (CX uses point diff, win rate uses
   * percentage-point diff). `null` hides the delta pill — used when
   * either side of the comparison is missing.
   */
  diff: number | null;
  /**
   * Pre-formatted display for the diff (e.g. `"+3"`, `"-2pp"`). The
   * caller owns the sign and unit; the card only decides the tone
   * (green / red / gray) from the sign and `indicatorType`.
   */
  formatDelta: (diff: number) => string;
  /**
   * Per-bucket points for the sparkline, oldest first. Empty array
   * renders the "no data" placeholder.
   */
  points: readonly MetricTrendPoint[];
  yDomain: readonly [number, number];
  /**
   * Formats a sparkline value for the hover tooltip. Caller decides
   * the unit (e.g. `"62%"` vs `"71"`); `null` renders as `"—"`.
   */
  formatTooltipValue: (value: number | null) => string;
  /**
   * `true` triggers the skeleton until the first payload arrives.
   * Subsequent refetches reuse the previous data and don't flash.
   */
  isLoading: boolean;
  /**
   * `true` when the trend payload arrived but the visible range had
   * no underlying records to score. Renders the "no data" placeholder
   * inside the chart well, leaving the headline as `"—"`.
   */
  hasNoData: boolean;
  /** Visible date range — used for the period length and axis labels. */
  dateRange: DateRange;
}

function periodLengthDays(dateRange: DateRange): number {
  const start = DateTime.fromISO(dateRange.after).toUTC();
  const end = DateTime.fromISO(dateRange.before).toUTC();
  if (!start.isValid || !end.isValid) {
    return 0;
  }
  // Inclusive on both ends to match how filter chips read (a
  // "Last 90 days" preset spans 90 calendar days, not 89).
  return Math.max(
    1,
    Math.floor(end.startOf("day").diff(start.startOf("day"), "days").days) + 1,
  );
}

interface AxisLabels {
  start: string;
  end: string;
}

/**
 * X-axis endpoint labels under the chart. When the date filter is
 * anchored to "now" (any "Last N days" preset), render the relative
 * form so the card matches how the user picked the range. For a
 * fixed custom range, render absolute dates instead so the axis
 * doesn't lie about what window is on screen.
 */
function axisLabels(dateRange: DateRange, now: DateTime): AxisLabels {
  const start = DateTime.fromISO(dateRange.after).toUTC();
  const end = DateTime.fromISO(dateRange.before).toUTC();
  const days = periodLengthDays(dateRange);
  const anchoredToToday = Math.abs(now.toUTC().diff(end, "days").days) < 1;
  if (anchoredToToday) {
    return { start: `${days}d ago`, end: "Today" };
  }
  return {
    start: start.toFormat("MMM d"),
    end: end.toFormat("MMM d"),
  };
}

export const MetricTrendCard: FC<MetricTrendCardProps> = ({
  label,
  headlineValue,
  diff,
  formatDelta,
  points,
  yDomain,
  formatTooltipValue,
  isLoading,
  hasNoData,
  dateRange,
}) => {
  const gradientId = useId();
  const now = useNow();

  const periodDays = periodLengthDays(dateRange);
  const axis = axisLabels(dateRange, now);

  const indication = computeValueChangeIndication(diff ?? 0, "positive");

  if (isLoading) {
    return <MetricTrendCardSkeleton />;
  }

  return (
    <section
      aria-label={label}
      className="flex flex-col gap-3 overflow-hidden rounded-xl bg-white p-5 shadow-xs ring-1 ring-black/5"
    >
      <h2 className="text-sm font-medium text-gray-600">{label}</h2>

      <div className="flex gap-6">
        <div className="flex h-16 flex-col justify-center gap-2 sm:h-20">
          <span className="text-4xl leading-none font-semibold tracking-tight text-foreground tabular-nums">
            {headlineValue}
          </span>
          {diff !== null ? (
            <div className="flex items-center gap-1.5">
              <DeltaPill delta={formatDelta(diff)} tone={indication.type} />
              <span className="text-xs text-muted-foreground">
                vs prior {periodDays}d
              </span>
            </div>
          ) : (
            <Pill
              color={Color.Gray}
              size={Size.SMALL}
              label={
                <span className="inline-flex items-center gap-1 text-gray-600">
                  <IconInfoCircle className="h-3.5 w-3.5" />
                  Not enough data to compare yet
                </span>
              }
            />
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="h-16 sm:h-20">
            {hasNoData ? (
              <div className="flex h-full items-center justify-center text-xs text-gray-400">
                No data in this period
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={points.map((p) => ({ ...p, value: p.value ?? 0 }))}
                  margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
                >
                  <SparklineGradient
                    id={gradientId}
                    color={indication.sparklineColor}
                  />
                  <XAxis dataKey="date" hide />
                  <YAxis domain={[yDomain[0], yDomain[1]]} hide />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke={indication.sparklineColor}
                    strokeWidth={1}
                    fill={`url(#${gradientId})`}
                    dot={false}
                    activeDot={{
                      r: 3,
                      strokeWidth: 1,
                      stroke: indication.sparklineColor,
                      fill: indication.sparklineColor,
                    }}
                    isAnimationActive={false}
                  />
                  <Tooltip
                    content={(props: TooltipContentProps) => (
                      <MetricTrendTooltip
                        {...props}
                        formatValue={formatTooltipValue}
                      />
                    )}
                    cursor={{
                      stroke: "var(--color-gray-300)",
                      strokeWidth: 1,
                    }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="flex justify-between text-xs text-gray-400">
            <span>{axis.start}</span>
            <span>{axis.end}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

const MetricTrendTooltip: FC<
  TooltipContentProps & { formatValue: (v: number | null) => string }
> = ({ active, payload, formatValue }) => {
  const entry = payload?.at(0);
  if (!active || !entry) return null;
  const point = entry.payload as MetricTrendPoint;
  const dateLabel = DateTime.fromISO(point.date, {
    zone: "utc",
  }).toLocaleString(DateTime.DATE_MED);
  return (
    <BaseTooltipContent
      label={dateLabel}
      entries={[
        {
          color: typeof entry.color === "string" ? entry.color : undefined,
          value: formatValue(point.value),
        },
      ]}
    />
  );
};

const MetricTrendCardSkeleton: FC = () => (
  <section className="flex flex-col gap-3 overflow-hidden rounded-xl bg-white p-5 shadow-xs ring-1 ring-black/5">
    <div className={skeletonStyle({ className: "h-4 w-40" })} />
    <div className={skeletonStyle({ className: "h-10 w-24" })} />
    <div className={skeletonStyle({ className: "h-16 w-full rounded-lg" })} />
  </section>
);
